import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  AclSettingOption,
  ChannelInfo,
  GroupOption,
  LanguageOption,
  LocationOption,
  ModuleOption,
  TtsPlanOption,
  VoicePlanOption,
} from './types';

const FIVE_MIN = 5 * 60 * 1000;

export const lookupKeys = {
  voicePlans: (parentId: string | number) => ['lookups', 'voice-plans', parentId] as const,
  ttsPlans: (parentId: string | number) => ['lookups', 'tts-plans', parentId] as const,
  modulesAll: () => ['lookups', 'modules-all'] as const,
  modulesForUser: (userId: string | number) => ['lookups', 'modules-for', userId] as const,
  channels: (userId: string | number) => ['lookups', 'channels', userId] as const,
  languages: (parentId: string | number | 'all') => ['lookups', 'languages', parentId] as const,
  aclList: (parentId: string | number) => ['lookups', 'acl', parentId] as const,
  locations: () => ['lookups', 'locations'] as const,
  groups: () => ['lookups', 'groups'] as const,
};

/** GET /api/obd/plans/list/{parentId} */
export function useVoicePlans(parentId: string | number | null) {
  return useQuery({
    queryKey: lookupKeys.voicePlans(parentId ?? 'none'),
    queryFn: () => api.get<VoicePlanOption[]>(`/api/obd/plans/list/${parentId}`),
    enabled: Boolean(parentId),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<VoicePlanOption>(data),
  });
}

/** GET /api/obd/tts/plans/list/{parentId} */
export function useTtsPlans(parentId: string | number | null) {
  return useQuery({
    queryKey: lookupKeys.ttsPlans(parentId ?? 'none'),
    queryFn: () => api.get<TtsPlanOption[]>(`/api/obd/tts/plans/list/${parentId}`),
    enabled: Boolean(parentId),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<TtsPlanOption>(data),
  });
}

/** GET /api/obd/module — every module that exists on the platform. */
export function useAllModules() {
  return useQuery({
    queryKey: lookupKeys.modulesAll(),
    queryFn: () => api.get<ModuleOption[]>('/api/obd/module'),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<ModuleOption>(data),
  });
}

/** GET /api/obd/module/list/{userId} — modules assigned to that user. */
export function useUserModules(userId: string | number | null) {
  return useQuery({
    queryKey: lookupKeys.modulesForUser(userId ?? 'none'),
    queryFn: () => api.get<ModuleOption[]>(`/api/obd/module/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<ModuleOption>(data),
  });
}

/** GET /api/obd/user/channels/{userId} — channel allocation. */
export function useUserChannels(userId: string | number | null) {
  return useQuery({
    queryKey: lookupKeys.channels(userId ?? 'none'),
    queryFn: () => api.get<ChannelInfo>(`/api/obd/user/channels/${userId}`),
    enabled: Boolean(userId),
    staleTime: FIVE_MIN,
  });
}

/**
 * Languages — for ROLE_ADMIN the parent's assigned languages, otherwise
 * the platform-wide list. Matches legacy CreateUser.getLangList():
 *   ROLE_ADMIN → /api/obd/user/languages/{parentId}
 *   else      → /api/obd/languages   (NOTE: no `/user/` prefix)
 */
export function useLanguages(parentId: string | number | null) {
  const role = useAuthStore((s) => s.role);
  const isAdmin = role === USER_ROLES.ADMIN;
  return useQuery({
    queryKey: lookupKeys.languages(isAdmin ? (parentId ?? 'none') : 'all'),
    queryFn: () =>
      api.get<LanguageOption[]>(
        isAdmin ? `/api/obd/user/languages/${parentId}` : '/api/obd/languages',
      ),
    enabled: !isAdmin || Boolean(parentId),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<LanguageOption>(data),
  });
}

/* GET /api/obd/acl/setting/unallocated/list/{parentId}
 * Returns only ACLs not already assigned to another sub-superadmin.
 * Pass excludeUserId when editing — keeps the current user's ACL visible.
 */
export function useAclSettings(
  parentId: string | number | null,
  excludeUserId?: string | number,
) {
  return useQuery({
    queryKey: [...lookupKeys.aclList(parentId ?? 'none'), excludeUserId ?? 0],
    queryFn: () => {
      const base = `/api/obd/acl/setting/unallocated/list/${parentId}`;
      const url = excludeUserId ? `${base}?excludeUserId=${excludeUserId}` : base;
      return api.get<AclSettingOption[]>(url);
    },
    enabled: Boolean(parentId),
    staleTime: FIVE_MIN,
    select: (data) =>
      ensureArray<AclSettingOption>(data).map((d: any) => ({
        aclId: d.id ?? d.aclId,
        aclName: d.aclName,
      })),
  });
}

/** GET /api/obd/locations — global locations list. */
export function useLocations() {
  return useQuery({
    queryKey: lookupKeys.locations(),
    queryFn: () => api.get<LocationOption[]>('/api/obd/locations'),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<LocationOption>(data),
  });
}

/** GET /api/obd/groups/{userId} — groups for logged-in user. */
export function useGroups() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: [...lookupKeys.groups(), userId],
    queryFn: () => api.get<GroupOption[]>(`/api/obd/groups/${userId}`),
    enabled: Boolean(userId),
    staleTime: FIVE_MIN,
    select: (data) => ensureArray<GroupOption>(data),
  });
}
